/**
 * The original's popup board — `OTHER OPTIONS` and every confirmation dialog
 * use the same widget (`docs/game/ui-reference/other-options-popup.png`,
 * `dialog-who-will-you-fly-for.png`). Colors, ring structure, row pitch and
 * fonts are measured off the capture; the *size and position* are computed
 * from the content instead of copied, because the shell needs popups the
 * original never had ("this screen is not in part 1"). That is a deliberate,
 * bounded liberty: the style is the original's, the box is ours.
 */

import {
  bevelRings,
  type BevelStack,
  type DrawOp,
  fill,
  type FontId,
  LOGICAL_HEIGHT,
  LOGICAL_WIDTH,
  type Rect,
  rectContains,
  ringRect,
  type TextStyle,
} from "./draw-ops.ts";
import { sunkenWellOps } from "./plate-chrome.ts";
import { TAN_231, TAN_232, TAN_233, TAN_234, TAN_236, TAN_FACE, TAN_LIGHTEST } from "./ps-palette.ts";
import { centerTextX, type FontAtlas, measureText, wrapText } from "./text-metrics.ts";

const POPUP_RINGS: BevelStack = [
  [TAN_LIGHTEST, TAN_232, TAN_232],
  [TAN_FACE, TAN_FACE, TAN_FACE],
  [TAN_234, TAN_LIGHTEST, TAN_LIGHTEST],
];

export const POPUP_TITLE_FONT: FontId = "FONT1";
export const POPUP_ROW_FONT: FontId = "FONT3";
export const POPUP_TITLE_STYLE: TextStyle = { body: TAN_231, shadow: TAN_234, highlight: 225 };
export const POPUP_ROW_STYLE: TextStyle = { body: 230, shadow: TAN_233, highlight: TAN_LIGHTEST };
export const POPUP_BODY_STYLE: TextStyle = { body: TAN_233, shadow: TAN_236, highlight: 227 };

export const POPUP_SIDE_PADDING = 13;
export const POPUP_TITLE_OFFSET_Y = 5;
export const POPUP_FIRST_ROW_OFFSET_Y = 21;
export const POPUP_ROW_HEIGHT = 9;
export const POPUP_ROW_PITCH = 12;
export const POPUP_BODY_LINE_PITCH = 9;
const POPUP_BOTTOM_PADDING = 5;
const POPUP_MIN_WIDTH = 110;

/**
 * The widest board the original ever puts on this screen, measured across the
 * capture corpus: `dialog-mission-not-complete.png` is 196 logical pixels wide
 * (`other-options-popup.png` is 144, `dialog-who-will-you-fly-for.png` 155,
 * `dialog-save-to-tape.png` 191). That same capture is also the evidence that
 * the original **wraps** a popup's prose — *"YOUR MISSION IS NOT COMPLETE. DO
 * YOU WISH TO:"* is drawn as two rows inside the board.
 *
 * The port had neither: `popupWidth` sized the box to its widest body line with
 * no cap at all, so END OF CAREER's narrative and epitaph — real sentences, not
 * menu labels — grew a box far wider than the 320-pixel screen and ran off both
 * edges. Capping and wrapping are one fix, because the cap is what gives the
 * wrap a column.
 */
export const POPUP_MAX_WIDTH = 196;

export interface PopupContent {
  readonly title: string;
  readonly body: readonly string[];
  readonly labels: readonly string[];
  /** `ShellDialog.minWidth` — a measured board width, for the one dialog the original actually has. */
  readonly minWidth?: number;
  /** `ShellDialog.footer` — the `ESC` board's `SCORE:` well. */
  readonly footer?: string;
}

/**
 * The `SCORE:` well under the `ESC` board's buttons, measured off
 * `docs/game/ui-reference/dialog-mission-not-complete.png`: **58 × 13** logical
 * pixels at x 131–188, y 162–174, two sunken rings around a black floor with the
 * figure in red.
 *
 * The rings are the shell's own `SUNKEN_RINGS`, and that is **measured, not
 * assumed**: every colour in the capture resolves to a `PS.PAL` index at
 * distance 0, and the well's outer ring reads 236/226/234 against the inner's
 * 233/224/232 — `SUNKEN_RINGS` exactly, ring for ring. Only the size and the
 * text colour are new. The width is a *minimum*: the capture shows a one-digit
 * score, and a four-digit one has to fit the same well rather than spill out.
 */
export const POPUP_FOOTER_SIZE = { w: 58, h: 13 } as const;
export const POPUP_FOOTER_GAP_Y = 4;
/**
 * Black floor, red figures — the only place in the shell's popup vocabulary that
 * leaves the tan ramp. `PS.PAL` **218** is `rgb(199, 0, 0)`, matched to the
 * capture at distance 0; the floor is the same index-0 black `WELL_FILL` already
 * uses, so it is taken from there rather than restated.
 */
export const POPUP_FOOTER_TEXT = 218;
export const POPUP_FOOTER_STYLE: TextStyle = { body: POPUP_FOOTER_TEXT };

export interface PopupLayout {
  readonly frame: Rect;
  readonly titleX: number;
  readonly titleY: number;
  readonly bodyX: number;
  readonly bodyTop: number;
  readonly rows: readonly Rect[];
  /**
   * `content.body` wrapped to the board's own column. Paint *this*, not
   * `content.body`: a caller hands the popup prose, and where that prose breaks
   * is a claim about glyph advances that only this layer can settle — the
   * dialogs come from `SurfaceController.dialog()`, which is deliberately given
   * no atlas.
   */
  readonly body: readonly string[];
  /** The `SCORE:` well's rect, centred under the rows. `undefined` when the dialog has no footer. */
  readonly footer?: Rect;
}

export interface PopupFonts {
  readonly title: FontAtlas;
  readonly row: FontAtlas;
}

/**
 * Rows are as wide as the widest label plus the same 6px breathing room the
 * capture shows, never narrower than the board and never wider than the widest
 * board the original draws. The body does not get a vote: it is wrapped to
 * whatever column the title and the labels settle on.
 */
function popupWidth(content: PopupContent, fonts: PopupFonts): number {
  const widest = Math.max(measureText(fonts.title, content.title), ...content.labels.map((label) => measureText(fonts.row, label)), 0);
  const floor = Math.max(POPUP_MIN_WIDTH, content.minWidth ?? 0);
  return Math.min(POPUP_MAX_WIDTH, Math.max(floor, widest + 2 * POPUP_SIDE_PADDING + 12));
}

/** What is printed when even a wrapped body will not fit the screen. */
export const POPUP_ELLIPSIS = "...";

/**
 * How many wrapped rows fit above the buttons without the board growing past
 * the screen it is centred on. Wrapping trades width for height, so a body that
 * used to run off the sides could otherwise run off the top and bottom instead;
 * this is the same fit-to-box rule the briefing plate uses, and the same
 * ellipsis when there was more.
 */
function fitBody(body: readonly string[], labels: number): readonly string[] {
  const room = LOGICAL_HEIGHT - POPUP_FIRST_ROW_OFFSET_Y - labels * POPUP_ROW_PITCH - POPUP_BOTTOM_PADDING;
  const rows = Math.max(1, Math.floor(room / POPUP_BODY_LINE_PITCH));
  if (body.length <= rows) return body;
  return [...body.slice(0, rows - 1), POPUP_ELLIPSIS];
}

export function layoutPopup(content: PopupContent, fonts: PopupFonts): PopupLayout {
  const width = popupWidth(content, fonts);
  const wrapped = content.body.flatMap((line) => wrapText(fonts.row, line, width - 2 * POPUP_SIDE_PADDING));
  const body = fitBody(wrapped, content.labels.length);
  const bodyHeight = body.length * POPUP_BODY_LINE_PITCH;
  const rowsHeight = content.labels.length * POPUP_ROW_PITCH;
  const footerHeight = content.footer === undefined ? 0 : POPUP_FOOTER_GAP_Y + POPUP_FOOTER_SIZE.h;
  const height = POPUP_FIRST_ROW_OFFSET_Y + bodyHeight + rowsHeight + footerHeight + POPUP_BOTTOM_PADDING;
  const frame: Rect = {
    x: Math.floor((LOGICAL_WIDTH - width) / 2),
    y: Math.floor((LOGICAL_HEIGHT - height) / 2),
    w: width,
    h: height,
  };
  const bodyTop = frame.y + POPUP_FIRST_ROW_OFFSET_Y - 3;
  const rowsTop = frame.y + POPUP_FIRST_ROW_OFFSET_Y + bodyHeight;
  return {
    frame,
    body,
    titleX: centerTextX(fonts.title, content.title, frame.x + Math.floor(frame.w / 2)),
    titleY: frame.y + POPUP_TITLE_OFFSET_Y,
    bodyX: frame.x + POPUP_SIDE_PADDING,
    bodyTop,
    rows: content.labels.map((_, index) => ({
      x: frame.x + POPUP_SIDE_PADDING,
      y: rowsTop + index * POPUP_ROW_PITCH,
      w: frame.w - 2 * POPUP_SIDE_PADDING,
      h: POPUP_ROW_HEIGHT,
    })),
    footer:
      content.footer === undefined
        ? undefined
        : {
            // Centred on the board rather than on the button column, which is
            // where the capture puts it: the well is narrower than the rows and
            // sits under them, not among them.
            x: frame.x + Math.floor((frame.w - footerWidth(content.footer, fonts.row)) / 2),
            y: rowsTop + content.labels.length * POPUP_ROW_PITCH + POPUP_FOOTER_GAP_Y,
            w: footerWidth(content.footer, fonts.row),
            h: POPUP_FOOTER_SIZE.h,
          },
  };
}

/** The measured 58 px, or enough to hold a longer figure — never narrower than the capture's well. */
function footerWidth(text: string, font: FontAtlas): number {
  return Math.max(POPUP_FOOTER_SIZE.w, measureText(font, text) + 10);
}

export function popupRowAt(layout: PopupLayout, x: number, y: number): number | undefined {
  const index = layout.rows.findIndex((rect) => rectContains(rect, x, y));
  return index < 0 ? undefined : index;
}

function popupRowOps(label: string, rect: Rect, pressed: boolean, font: FontAtlas): DrawOp[] {
  const ring: readonly [number, number, number] = pressed
    ? [TAN_236, TAN_LIGHTEST, TAN_233]
    : [TAN_LIGHTEST, TAN_236, TAN_233];
  return [
    ...ringRect(rect, ring[0], ring[1], ring[2]),
    fill({ x: rect.x + 1, y: rect.y + 1, w: rect.w - 2, h: rect.h - 2 }, 227),
    {
      kind: "text",
      font: POPUP_ROW_FONT,
      text: label,
      x: centerTextX(font, label, rect.x + Math.floor(rect.w / 2)),
      y: rect.y + (pressed ? 3 : 2),
      style: POPUP_ROW_STYLE,
    },
  ];
}

export function popupOps(content: PopupContent, layout: PopupLayout, selected: number, fonts: PopupFonts): DrawOp[] {
  const { frame } = layout;
  const ops: DrawOp[] = [
    fill({ x: frame.x + 3, y: frame.y + 3, w: frame.w - 6, h: frame.h - 6 }, TAN_FACE),
    ...bevelRings(frame, POPUP_RINGS),
    {
      kind: "text",
      font: POPUP_TITLE_FONT,
      text: content.title,
      x: layout.titleX,
      y: layout.titleY,
      style: POPUP_TITLE_STYLE,
    },
  ];
  layout.body.forEach((line, index) =>
    ops.push({
      kind: "text",
      font: POPUP_ROW_FONT,
      text: line,
      x: layout.bodyX,
      y: layout.bodyTop + index * POPUP_BODY_LINE_PITCH,
      style: POPUP_BODY_STYLE,
    }),
  );
  layout.rows.forEach((rect, index) => {
    const label = content.labels[index];
    if (label !== undefined) ops.push(...popupRowOps(label, rect, index === selected, fonts.row));
  });
  if (layout.footer && content.footer !== undefined) {
    ops.push(...sunkenWellOps(layout.footer));
    ops.push({
      kind: "text",
      font: POPUP_ROW_FONT,
      text: content.footer,
      x: centerTextX(fonts.row, content.footer, layout.footer.x + Math.floor(layout.footer.w / 2)),
      y: layout.footer.y + 3,
      style: POPUP_FOOTER_STYLE,
    });
  }
  return ops;
}
